import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Sprint 12 — Global ErrorBoundary.
 *
 * Catches render-time errors anywhere below in the tree and shows a
 * branded recovery card instead of a white screen. Use this around any
 * surface that drives mission-critical UX (portal, candidate session,
 * client report).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
      try { window.Sentry.captureException(error, { extra: info }); } catch { /* ignore */ }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    if (typeof window === 'undefined') return;
    if (this.props.candidateMode) {
      // Unrecoverable candidate session — do not send OTC users to /portal.
      window.location.assign('/');
      return;
    }
    window.location.assign('/portal');
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const errMsg = this.state.error?.message || 'An unexpected error occurred.';
    const candidateMode = Boolean(this.props.candidateMode);
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6" data-testid="error-boundary">
        <Card className="card-elevated max-w-lg w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 ring-1 ring-rose-200">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Something went wrong</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {candidateMode
                  ? 'A temporary error occurred. Try again. If this keeps happening, contact HR for a new invitation link.'
                  : 'The page hit an unexpected error. Try reloading; if the issue keeps happening, contact your administrator.'}
              </p>
              <p className="mt-3 text-xs text-rose-700 font-mono bg-rose-50 border border-rose-200 rounded p-2 text-left">
                {errMsg}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={this.handleReset} className="gap-1.5" data-testid="error-boundary-retry">
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </Button>
              <Button size="sm" onClick={this.handleGoHome} className="gap-1.5" data-testid="error-boundary-home">
                <Home className="h-3.5 w-3.5" /> {candidateMode ? 'Exit' : 'Back to portal'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
